import { useState } from "react";
import CryptoJS from "crypto-js";
import { Text, HStack, VStack, Box, useMediaQuery, Pressable } from "native-base";
import Stars from "../../components/stars";
import Svg from "react-native-svg";
import { approvalTickIconSVG, profileAvatarSVG, deleteCardsIconSVG } from "../../utils/svg";
import DividerLine from "../../components/divider";
import { COLORS } from "../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../utils/fonts";
import PopOverModal from "../../components/popover";
import { hasPermission } from "../../utils/permission";
import { MOBILE_VIEW_WIDTH } from "../../utils/screens";
import MobileModelSheet from "../../components/actionSheet";
import CustomModal from "../../components/modal";
import CustomButton from "../../components/button";
import config from "../../utils/config";
import { deleteImageReviewAPI } from "../../api/apis/workflow";
import { Toaster } from "../../components/toaster";
import { LoaderAnimation } from "../../components/spinner";

const Comments = ({
  showAll = true,
  stars = 0,
  comments = "",
  approval = false,
  reviewPayload,
  approveReviewWorkflow,
  getReviews,
  getEventDetails,
  scrollToTop = () => {},
  readAll = false,
  showDeleteIcon = true,
}) => {
  const bytes = CryptoJS.AES.decrypt(
    sessionStorage.getItem("content"),
    config.userKey
  );
  const jsonString = bytes.toString(CryptoJS.enc.Utf8);
  const userInfo = JSON.parse(jsonString);
  const toaster = Toaster();
  const [acceptPopup, setAcceptPopup] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH,
  });

  const onClose = () => {
    setAcceptPopup(false);
    setRejectPopup(false);
    setDeletePopup(false);
  };

  const getDeleteModalBody = () => {
    return (
      <Box>
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
        >
          Are you sure you want to delete this review?
        </Text>
      </Box>
    );
  };

  const getDeleteModalFooter = () => {
    return (
      <HStack
        justifyContent={'end'}
        width={'100%'}
        space={4}
      >
        <CustomButton
          onPress={() => setDeletePopup(false)}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleDeleteReview}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };

  const updateReviewStatus = async (step, action_name) => {
    let id = reviewPayload["_id"];
    reviewPayload.workflow_instance_id = reviewPayload["_id"];
    reviewPayload.approved_at = new Date();
    reviewPayload.approved_by = userInfo.user_id;
    delete reviewPayload["_id"];
    readAll
      ? await approveReviewWorkflow([reviewPayload], step, id, action_name)
      : await approveReviewWorkflow(reviewPayload, step, id, action_name);
    await getReviews();
    onClose();
  };

  const handleDeleteReview = async () => {
    if (!reviewPayload?._id) {
      toaster('Review ID not found', 'error', 'error');
      return;
    }

    setIsDeleting(true);
    try {
      LoaderAnimation.show();
      
      const payload = { workflow_instance_ids: [reviewPayload._id] };
      const response = await deleteImageReviewAPI(payload);

      if (response && response?.error) {
        toaster(response?.message || 'Failed to delete review', 'error', 'error');
      } else {
        const successMessage = 'Review deleted successfully';
        toaster(successMessage, 'success', 'success');
        
        // Refresh reviews list and event details
        if (typeof getReviews === 'function') {
          await getReviews();
        }
        if (typeof getEventDetails === 'function') {
          await getEventDetails();
        }
      }
    } catch (error) {
      toaster('An error occurred while deleting review', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
      setDeletePopup(false);
    }
  };

  const getFormattedDate = (dates) => {
    const date = new Date(dates);
    if (isNaN(date.getTime())) {
      return;
    }

    const formattedDate = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      // timeZone: "UTC"
    }).format(date);

    return formattedDate;
  };

  // Check if user can delete this review (author or admin)
  const canDeleteReview = reviewPayload?.added_by === userInfo?.user_id || userInfo?.role === 'admin';

  return (
    <HStack mb={4} space={4} pl={4} pr={4} pt={2} position="relative" width={"100%"}>
      <Box mt={2}>
        <Svg width="30" height="35" viewBox="0 0 20 20">
          {profileAvatarSVG}
        </Svg>
      </Box>
      <div mt={2} space={1} flexWrap={"wrap"} style={{ flex: 1, position: "relative" }}>
        <VStack flex={1} width="100%">
          <Box flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <Text
              color={COLORS.BLACK_COLOR}
              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
            >
              {reviewPayload.added_employee_name &&
                reviewPayload.added_employee_name}
            </Text>
          
          {/* Delete Button - Positioned at absolute right corner */}
            {showDeleteIcon && canDeleteReview && (
              <Pressable
                onPress={() => {
                  !MobileWindow && scrollToTop();
                  setDeletePopup(true);
                }}
                // position="absolute"
                // top={0}
                // right={0}
                zIndex={1}
                // alignSelf={'flex-end'}
              >
                <Svg width="18" height="18" viewBox="0 0 24 24">
                  {deleteCardsIconSVG}
                </Svg>
              </Pressable>
            )}
          </Box>
            
            <Stars stars={stars} editable={false} />
            <Text width={"inherit"} style={{ wordBreak: "break-all" }}>
              {comments}
            </Text>
            {hasPermission("LANDING_PAGE") && (
              <HStack mt={2}>
                {(reviewPayload.status === "pending" ||
                  reviewPayload.status === "review_approved") && (
                  <>
                    <Text color={COLORS.NOTIFICATION_TIME_COLOR}>
                      {reviewPayload.added_at &&
                        getFormattedDate(reviewPayload.added_at)}
                    </Text>
                    {reviewPayload.status === "pending" && (
                      <>
                        <DividerLine height="19px" />
                        <Text color={COLORS.REVIEWS_UNDERREVIEW_COLOR}>
                          Under Review
                        </Text>
                      </>
                    )}
                  </>
                )}
              </HStack>
            )}
          </VStack>



         

        {hasPermission("REVIEW") && (
          <>
            {/* {reviewPayload.status === "review_approved" && (
              <>
                <HStack mt={2}>
                  <Text color={COLORS.NOTIFICATION_TIME_COLOR}>
                    {reviewPayload.added_at &&
                      getFormattedDate(reviewPayload.added_at)}
                  </Text>
                  // <DividerLine height="19px" />
                  // <HStack space={2} alignItems={"center"}>
                  //   <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>Approved</Text>
                  //   <Svg width="15" height="12" viewBox="0 0 15 12">
                  //     {approvalTickIconSVG}
                  //   </Svg>
                  // </HStack>
                </HStack>
              </>
            )} */}

            {reviewPayload.status === "review_rejected" && (
              <>
                <HStack space={2} pt={3} alignItems={"center"}>
                  <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>Rejected</Text>
                  <Svg width="15" height="12" viewBox="0 0 15 12">
                    {approvalTickIconSVG}
                  </Svg>
                </HStack>
              </>
            )}

            {reviewPayload.status === "pending" && (
              <VStack space={2} mt={2}>
                <Text color={COLORS.NOTIFICATION_TIME_COLOR}>
                  {reviewPayload.added_at &&
                    getFormattedDate(reviewPayload.added_at)}
                </Text>
                <HStack>
                  {!MobileWindow ? (
                    <PopOverModal
                      isOpen={acceptPopup}
                      onClose={onClose}
                      type="comments"
                      svg={
                        <Text
                          onPress={() => {
                            setAcceptPopup(true);
                          }}
                          color={COLORS.APPROVE_TEXT_COLOR}
                          underline
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          style={{ cursor: "pointer" }}
                        >
                          Approve
                        </Text>
                      }
                      body={<p>Are you sure you want to Approve?</p>}
                      footer={
                        <HStack
                          my={3}
                          justifyContent={"space-evenly"}
                          width={"100%"}
                        >
                          <Text
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            onPress={onClose}
                            style={{ cursor: "pointer" }}
                          >
                            Cancel
                          </Text>
                          <Text
                            onPress={() => {
                              updateReviewStatus(
                                "REVIEW_APPROVAL",
                                "APPROVE_REVIEWS"
                              );
                            }}
                            color={COLORS.APPROVE_TEXT_COLOR}
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            fontWeight={
                              COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                            }
                            style={{ cursor: "pointer" }}
                          >
                            Approve
                          </Text>
                        </HStack>
                      }
                    />
                  ) : (
                    <>
                      <Text
                        onPress={() => {
                          setAcceptPopup(true);
                        }}
                        color={COLORS.APPROVE_TEXT_COLOR}
                        underline
                        fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                        style={{ cursor: "pointer" }}
                      >
                        Approve
                      </Text>
                      <MobileModelSheet
                        isOpen={acceptPopup}
                        onClose={onClose}
                        body={<p>Are you sure you want to Approve?</p>}
                        footer={
                          <HStack
                            my={3}
                            justifyContent={"space-evenly"}
                            width={"100%"}
                          >
                            <Text
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                              onPress={onClose}
                              style={{ cursor: "pointer" }}
                            >
                              Cancel
                            </Text>
                            <Text
                              onPress={() => {
                                updateReviewStatus(
                                  "REVIEW_APPROVAL",
                                  "APPROVE_REVIEWS"
                                );
                              }}
                              color={COLORS.APPROVE_TEXT_COLOR}
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                              fontWeight={
                                COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                              }
                              style={{ cursor: "pointer" }}
                            >
                              Approve
                            </Text>
                          </HStack>
                        }
                      />
                    </>
                  )}
                  <DividerLine direction="vertical" />
                  {!MobileWindow ? (
                    <PopOverModal
                      isOpen={rejectPopup}
                      onClose={onClose}
                      type="comments"
                      svg={
                        <Text
                          onPress={() => {
                            setRejectPopup(true);
                          }}
                          color={COLORS.REJECT_TEXT_COLOR}
                          underline
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          style={{ cursor: "pointer" }}
                        >
                          Reject
                        </Text>
                      }
                      body={<p>Are you sure you want to Reject?</p>}
                      footer={
                        <HStack
                          my={3}
                          justifyContent={"space-evenly"}
                          width={"100%"}
                        >
                          <Text
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            onPress={onClose}
                            style={{ cursor: "pointer" }}
                          >
                            Cancel
                          </Text>
                          <Text
                            onPress={() => {
                              updateReviewStatus(
                                "REVIEW_APPROVAL",
                                "REJECT_REVIEWS"
                              );
                            }}
                            color={COLORS.REJECT_TEXT_COLOR}
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            fontWeight={
                              COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                            }
                            style={{ cursor: "pointer" }}
                          >
                            Reject
                          </Text>
                        </HStack>
                      }
                    />
                  ) : (
                    <>
                      <Text
                        onPress={() => {
                          setRejectPopup(true);
                        }}
                        color={COLORS.REJECT_TEXT_COLOR}
                        underline
                        fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                        style={{ cursor: "pointer" }}
                      >
                        Reject
                      </Text>
                      <MobileModelSheet
                        isOpen={rejectPopup}
                        onClose={onClose}
                        body={<p>Are you sure you want to Reject?</p>}
                        footer={
                          <HStack
                            my={3}
                            justifyContent={"space-evenly"}
                            width={"100%"}
                          >
                            <Text
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                              onPress={onClose}
                              style={{ cursor: "pointer" }}
                            >
                              Cancel
                            </Text>
                            <Text
                              onPress={() => {
                                updateReviewStatus(
                                  "REVIEW_APPROVAL",
                                  "REJECT_REVIEWS"
                                );
                              }}
                              color={COLORS.REJECT_TEXT_COLOR}
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                              fontWeight={
                                COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                              }
                              style={{ cursor: "pointer" }}
                            >
                              Reject
                            </Text>
                          </HStack>
                        }
                      />
                    </>
                  )}
                </HStack>
              </VStack>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={deletePopup}
          onClose={() => setDeletePopup(false)}
          header="Delete Review"
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          width="40%"
        />
      ) : (
        <MobileModelSheet
          isOpen={deletePopup}
          onClose={() => setDeletePopup(false)}
          header="Delete Review"
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          maxH="40vh"
        />
      )}
    </HStack>
  );
};

export default Comments;
